import React, {
  useState,
  useEffect,
  useCallback,
  FunctionComponent,
} from 'react';
import qs from 'qs';
import { RouteComponentProps } from 'react-router-dom';
import { isEqual } from 'lodash';

import useCurrentUser from '../../utils/hooks/useCurrentUser';
import {
  AdminDashboardTab,
  AdminDashboardTableHeaderRowValueInterface,
  AttributesInterface,
  DateAttributesInterface,
  Option,
  SearchState,
} from './admin-dashboard-tables/interfaces';
import AdminDashboardTable from './admin-dashboard-tables/AdminDashboardTable';
import { mergeOverlappingFacets, removeDuplicatesFromStringArray } from './utils';

const createURL = (state) => `?${qs.stringify(state)}`;

const searchStateToUrl = (location: { pathname: string }, searchState: SearchState) => {
  if (searchState) {
    return `${location.pathname}${createURL(searchState)}`;
  }

  return '';
};

const urlToSearchState = (location: { search: string }): SearchState|{} => {
  return location ? qs.parse(location.search.slice(1)) : {};
};

interface Props extends RouteComponentProps {
  title?: string,
  externalQueryParams?: Array<{key: string, value: string}>,
  indexName: string,
  tableHeaderRowValues?: AdminDashboardTableHeaderRowValueInterface[],
  customHits: any, // TODO: update `any`
  attributes: Array<AttributesInterface>,
  dateAttributes: Array<DateAttributesInterface>,
  externalSearchState?: SearchState,
  defaultFacetFilters?: Array<string|Array<string>>,
  defaultNumericFilters?: Array<string>,
  tabs: Array<AdminDashboardTab>,
  bulkActions?: Array<Option>,
  allSelected?: boolean,
  setHits?: (hits: Array<number>) => void,
  showSearch?: boolean,
  showReset?: boolean,
  setExtraData?: (hits: Array<any>) => void,
  toggleAllSelected?: (allSelected: boolean) => void,
  clearBulkSelected?: () => void,
  setBulkAction?: (action: Option) => void,
  bulkAction?: Option,
  useCards?: boolean,
  allowSetHitsPerPage?: boolean,
  ActionButton?: FunctionComponent,
  TitleComponent?: FunctionComponent,
  BodyComponent?: FunctionComponent,
  numEmptyStartHeaders?: number,
  numEmptyEndHeaders?: number,
  numUnhiddenEmptyEndHeaders?: number,
  allowBulkSelect?: boolean,
}

const AdminURLSync: React.FC<Props> = ({
  title,
  setBulkAction,
  bulkAction,
  externalQueryParams,
  indexName,
  tableHeaderRowValues,
  customHits,
  location,
  history,
  match,
  attributes,
  dateAttributes,
  externalSearchState,
  defaultFacetFilters,
  defaultNumericFilters,
  tabs,
  bulkActions,
  allSelected,
  setHits,
  showSearch,
  showReset,
  setExtraData,
  toggleAllSelected,
  clearBulkSelected,
  useCards,
  allowSetHitsPerPage,
  ActionButton,
  TitleComponent,
  BodyComponent,
  numEmptyStartHeaders,
  numEmptyEndHeaders,
  numUnhiddenEmptyEndHeaders,
  allowBulkSelect,
}) => {
  const setInitialFacetFilters = useCallback(() => {
    const initialFilters:Array<string|Array<string>> = [];

    if (defaultFacetFilters) {
      defaultFacetFilters.forEach((facFilter) => initialFilters.push(facFilter));
    }

    if (
      externalSearchState
      && externalSearchState.configure
      && externalSearchState.configure.facetFilters
    ) {
      externalSearchState.configure.facetFilters
        .forEach((facFilter) => initialFilters.push(facFilter));
    }
    return mergeOverlappingFacets(initialFilters);
  }, [defaultFacetFilters, externalSearchState]);

  const setInitialNumericFilters = useCallback(() => {
    const initialFilters:Array<string> = [];

    if (defaultNumericFilters) {
      defaultNumericFilters.forEach((numFilter) => initialFilters.push(numFilter));
    }

    if (
      externalSearchState
      && externalSearchState.configure
      && externalSearchState.configure.numericFilters
    ) {
      externalSearchState.configure.numericFilters
        .forEach((numFilter) => initialFilters.push(numFilter));
    }
    return removeDuplicatesFromStringArray(initialFilters);
  }, [defaultNumericFilters, externalSearchState]);

  const getInitialSearchState = (facetFilters, numericFilters) => {
    const defaultState = {
      configure: {
        hitsPerPage: 50,
        facetFilters,
        numericFilters,
      },
      query: '',
      page: 1,
      sortBy: indexName,
    };
    const stateFromUrl: SearchState = urlToSearchState(location);
    return {
      ...stateFromUrl,
      configure: {
        hitsPerPage: stateFromUrl?.configure?.hitsPerPage || defaultState.configure.hitsPerPage,
        facetFilters: mergeOverlappingFacets([
          ...defaultState.configure.facetFilters,
          ...(stateFromUrl?.configure?.facetFilters || []),
        ]),
        numericFilters: removeDuplicatesFromStringArray([
          ...defaultState.configure.numericFilters,
          ...(stateFromUrl?.configure?.numericFilters || []),
        ]),
      },
      query: stateFromUrl?.query || defaultState.query,
      page: stateFromUrl?.page || defaultState.page,
      sortBy: stateFromUrl?.sortBy || defaultState.sortBy,
    };
  };

  const [facetFilters, setFacetFilters] = useState<Array<string|Array<string>>>(
    setInitialFacetFilters(),
  );
  const [numericFilters, setNumericFilters] = useState<Array<string>>(
    setInitialNumericFilters(),
  );
  const [searchState, setSearchState] = useState<SearchState>(
    getInitialSearchState(facetFilters, numericFilters),
  );
  const [lastLocation, setLastLocation] = useState(location);
  const [defaultRefinement, setDefaultRefinement] = useState('');

  const { currentUser } = useCurrentUser();

  useEffect(() => {
    // When new filters are added, reset search to page 1
    if (searchState.page !== 1) {
      setSearchState((s) => ({ ...s, page: 1 }));
    }
  }, [facetFilters, numericFilters]);

  useEffect(() => {
    if (externalSearchState) {
      const newFacetFilters:Array<string|Array<string>> = [];
      const newNumericFilters:Array<string> = [];
      if (searchState.configure && searchState.configure.facetFilters) {
        searchState.configure.facetFilters
          .forEach((fac:string|Array<string>) => newFacetFilters.push(fac));
      }

      if (searchState.configure && searchState.configure.numericFilters) {
        searchState.configure.numericFilters
          .forEach((fac:string) => newNumericFilters.push(fac));
      }

      if (externalSearchState.configure && externalSearchState.configure.facetFilters) {
        externalSearchState.configure.facetFilters
          .forEach((fac:string|Array<string>) => newFacetFilters.push(fac));
      }
      if (externalSearchState.configure && externalSearchState.configure.numericFilters) {
        externalSearchState.configure.numericFilters
          .forEach((fac:string) => newNumericFilters.push(fac));
      }

      const newSearchState = {
        ...searchState,
        configure: {
          ...searchState.configure,
          facetFilters: mergeOverlappingFacets(newFacetFilters),
          numericFilters: removeDuplicatesFromStringArray(newNumericFilters),
        },
      };

      if (!isEqual(newSearchState, searchState)) {
        setSearchState(newSearchState);
      }
    }
  }, [searchState]);

  useEffect(() => {
    if (location !== lastLocation) {
      setSearchState(urlToSearchState(location));
      setLastLocation(location);
    }
  }, [lastLocation, setSearchState, setLastLocation]);

  useEffect(() => {
    const { configure } = searchState;
    if (configure) {
      const { facetFilters: fF, numericFilters: nF } = configure;
      if (fF) {
        setFacetFilters(fF);
      }
      if (nF) {
        setNumericFilters(nF);
      }
    }
  }, [searchState, setFacetFilters, setNumericFilters]);

  const onSearchStateChange = useCallback((newSearchState) => {
    if (!isEqual(searchState, newSearchState)) {
      history.push(
        searchStateToUrl(location, newSearchState),
        newSearchState,
      );
      setSearchState(newSearchState);
    }
  }, [searchState, setSearchState, history, searchStateToUrl]);

  const onReset = useCallback(() => {
    const newFacetFilters:Array<string|Array<string>> = [...(defaultFacetFilters || [])];
    const newNumericFilters:Array<string> = [...(defaultNumericFilters || [])];
    if (externalSearchState) {
      if (externalSearchState.configure && externalSearchState.configure.facetFilters) {
        externalSearchState.configure.facetFilters
          .forEach((fac:string|Array<string>) => newFacetFilters.push(fac));
      }
      if (externalSearchState.configure && externalSearchState.configure.numericFilters) {
        externalSearchState.configure.numericFilters
          .forEach((fac:string) => newNumericFilters.push(fac));
      }
    }
    const newFacetFilterSet = removeDuplicatesFromStringArray(newFacetFilters);
    const newNumericFilterSet = removeDuplicatesFromStringArray(newNumericFilters);
    setFacetFilters(newFacetFilterSet);
    setNumericFilters(newNumericFilterSet);
    setSearchState({
      ...searchState,
      configure: {
        facetFilters: newFacetFilterSet,
        hitsPerPage: 50,
        numericFilters: newNumericFilterSet,
      },
      query: '',
      page: 1,
      sortBy: defaultRefinement || indexName,
    });
    const query = externalQueryParams && externalQueryParams
      .map((param) => `${param.key}=${param.value}`)
      .join('&');
    history.push(query ? `${location.pathname}?${query}` : location.pathname);
  }, [
    setSearchState,
    setNumericFilters,
    setSearchState,
    externalSearchState,
    searchState,
    indexName,
    history,
    location,
    defaultFacetFilters,
    defaultNumericFilters,
  ]);

  return (
    <AdminDashboardTable
      currentUser={currentUser}
      ActionButton={ActionButton}
      TitleComponent={TitleComponent}
      BodyComponent={BodyComponent}
      title={title}
      externalQueryParams={externalQueryParams || []}
      searchState={searchState}
      onSearchStateChange={onSearchStateChange}
      createURL={createURL}
      history={history}
      location={location}
      facetFilters={facetFilters}
      setFacetFilters={setFacetFilters}
      numericFilters={numericFilters}
      setNumericFilters={setNumericFilters}
      indexName={indexName}
      defaultRefinement={defaultRefinement}
      setDefaultRefinement={setDefaultRefinement}
      tableHeaderRowValues={tableHeaderRowValues}
      allSelected={allSelected}
      toggleAllSelected={toggleAllSelected}
      clearBulkSelected={clearBulkSelected}
      customHits={customHits}
      match={match}
      attributes={attributes}
      dateAttributes={dateAttributes}
      onReset={onReset}
      tabs={tabs}
      setBulkAction={setBulkAction}
      bulkAction={bulkAction}
      bulkActions={bulkActions}
      setHits={setHits}
      showSearch={showSearch}
      showReset={showReset}
      setExtraData={setExtraData}
      useCards={useCards}
      allowSetHitsPerPage={allowSetHitsPerPage}
      numEmptyStartHeaders={numEmptyStartHeaders}
      numEmptyEndHeaders={numEmptyEndHeaders}
      numUnhiddenEmptyEndHeaders={numUnhiddenEmptyEndHeaders}
      allowBulkSelect={allowBulkSelect}
    />
  );
};

AdminURLSync.defaultProps = {
  attributes: [],
  dateAttributes: [],
  externalQueryParams: [],
  allowSetHitsPerPage: true,
  allowBulkSelect: true,
  showSearch: true,
  showReset: true,
  title: '',
};

export default AdminURLSync;
